import type { BotTier } from '@runkr/config'
import { ITEM, JUMP, PET } from '../inputBits'
import type { InputBits } from '../inputBits'
import { droneTarget } from '../items/homing_drone'
import { leaderOf } from '../items/leader_missile'
import { activeRide, canSummon } from '../player/rides'
import { nextRange } from '../rng'
import type { Rng } from '../rng'
import { NO_OBSTACLE, NO_PLAYER, NO_TICK } from '../world'
import type { BotAction, BotMemory, ItemId, World } from '../world'
import { crateAhead, jumpDodges, perceive, plan, playerAhead } from './perception'
import type { Obstacle, View } from './perception'
import { rollout } from './rollout'
import type { Outcome } from './rollout'
import { airJump, climb } from './routes'
import { clearSlip, drawObstacleTiming, ensurePersonality } from './variety'

/**
 * Tier-1 bot (ARCHITECTURE §2.7, PHASE1 T1.4): this tick's buttons for player `i`. Deterministic
 * in the world state and `rng`; it writes only `world.bots[i]` and draws from `rng` only when a
 * new obstacle appears (reaction delay) or an item is picked up (use delay). The caller passes
 * `world.rng.bots` and calls bots in player-index order before `stepWorld`, never from inside it.
 *
 * Always runs right (one speed for everyone, D-045). Jumps gaps, walls, hazards and traps at the
 * last tick a jump still clears them (a tap when a short hop does, else a full-height hold), and
 * in the air uses its double jump the same way once the forecast says it would otherwise fall or
 * be hit (D-044). Jumps over a homing drone locked on it, and keeps running onto a bounce pad.
 * At a fork it takes the upper route by its own chance (`routes.ts`) and stays up there. With a
 * full ride meter it summons its ride on open ground (T3.10).
 *
 * Variety (T1.4b): per obstacle an act-early offset (acts up to that many ticks before the last
 * safe tick, never at a tick that does not clear) and, by chance, a late slip that presses the
 * jump a few ticks after the last safe tick (a real mistake).
 */
export function botInput(world: World, i: number, tier: BotTier, rng: Rng): InputBits {
  const p = world.players[i]!
  const mem = world.bots[i]!
  ensurePersonality(mem, tier, rng)
  const helpless = p.stunnedUntil > world.tick || p.skidUntil > world.tick
  if (p.placement !== 0 || p.respawnAtTick !== NO_TICK || helpless) {
    mem.holdJump = false
    mem.airJump = false
    clearSlip(mem)
    // Back at a checkpoint the bot looks at the course afresh (new reaction and timing draws).
    if (p.respawnAtTick !== NO_TICK) mem.obstacleId = NO_OBSTACLE
    return 0
  }
  // A gliding rider rises at the lift speed for as long as JUMP is held: let go once the jump
  // itself has slowed to it, like at the apex of a normal jump.
  const ride = activeRide(world, p)
  const liftVy = ride?.ability === 'glide' ? -ride.glideLiftPxPerTick : 0
  if (p.grounded || p.vy >= liftVy) mem.holdJump = false

  const view = perceive(world, i, tier, mem.holdJump)
  notice(world, i, view, tier, rng)
  const bits = move(world, i, mem, view, tier, rng) | itemBits(world, i, mem, view, tier, rng)
  return bits | rideBits(world, i, view)
}

/**
 * A new obstacle in the look-ahead: draw its reaction delay and timing once. In the air after a
 * jump, a coming fall is the same gap the bot already reacted to (its forecast key moves with the
 * body), so it draws nothing. A new drone chasing the bot: draw a reaction delay for it.
 */
function notice(world: World, i: number, view: View, tier: BotTier, rng: Rng): void {
  const mem = world.bots[i]!
  const ob = view.obstacle
  const airborneFall = ob?.kind === 'fall' && !world.players[i]!.grounded
  if (ob && ob.id !== mem.obstacleId && !airborneFall) {
    mem.obstacleId = ob.id
    mem.reactAtTick = world.tick + nextRange(rng, tier.reactionMinTicks, tier.reactionMaxTicks)
    drawObstacleTiming(mem, tier, rng)
  }
  const drone = view.drone
  if (drone && drone.id !== mem.droneId) {
    mem.droneId = drone.id
    mem.droneReactAtTick = world.tick + nextRange(rng, tier.reactionMinTicks, tier.reactionMaxTicks)
  }
}

function move(
  world: World,
  i: number,
  mem: BotMemory,
  view: View,
  tier: BotTier,
  rng: Rng,
): InputBits {
  const planned = airJump(world, i, mem)
  if (planned !== null) return planned
  const bits = mem.holdJump ? JUMP : 0
  if (world.tick >= mem.droneReactAtTick && dodgeDrone(world, i, mem, view)) return bits | JUMP
  if (mem.slipAtTick !== NO_TICK) return bits | slip(world, mem)
  const up = climb(world, i, mem, view, tier, rng)
  if (up !== 0) return bits | up
  const ob = view.obstacle
  if (!ob) return bits | reachCrate(world, i, mem, view, tier)
  if (world.tick < mem.reactAtTick || ob.padFirst) return bits
  return bits | jumpOver(world, i, mem, view, ob)
}

/** Run a plan for player `i` in this view. */
function forecast(
  world: World,
  i: number,
  mem: BotMemory,
  view: View,
  action: 'tap' | 'hold',
  delay: number,
  goalX: number,
  groundOnly = false,
): Outcome {
  return rollout(world, i, view.scene, { ...plan(view, mem, action, delay, goalX), groundOnly })
}

/**
 * How many ticks ahead a move must no longer clear before the bot makes it now: 1 acts at the
 * last safe tick, `actEarlyTicks + 1` that much earlier. A pending late slip uses the last safe
 * tick as its reference.
 */
function waitProbe(mem: BotMemory): number {
  return mem.lateTicks > 0 ? 1 : mem.actEarlyTicks + 1
}

/** Make the move now, or with a late slip schedule it `lateTicks` after this (last safe) tick. */
function decide(world: World, mem: BotMemory, action: 'tap' | 'hold'): InputBits {
  if (mem.lateTicks > 0) {
    mem.slipAtTick = world.tick + mem.lateTicks
    mem.slipAction = action
    return 0
  }
  return press(mem, action)
}

/** Press the slipped move once its tick has come, whatever the forecast says by then. */
function slip(world: World, mem: BotMemory): InputBits {
  if (world.tick < mem.slipAtTick) return 0
  const action: BotAction = mem.slipAction
  clearSlip(mem)
  mem.lateTicks = 0
  return action === 'tap' || action === 'hold' ? press(mem, action) : 0
}

/**
 * Jump at the last tick a jump still clears the obstacle (or up to `actEarlyTicks` before it):
 * if a jump `waitProbe` ticks later would also clear it, wait. Try a tap first (short hops for
 * narrow gaps), then a full-height hold. When no jump clears it, act only at the last chance and
 * pick whatever delays the trouble most; against a wall, always the full-height jump (waiting
 * there never ends). In the air the same press is the double jump, so a gap
 * too wide for one jump is crossed by jumping at the edge and jumping again before the fall.
 */
function jumpOver(world: World, i: number, mem: BotMemory, view: View, ob: Obstacle): InputBits {
  // Already against it (a wall stops the runner): waiting would never get any later.
  const blocked = view.run.at === 0
  // Coyote time counts as the ground: its jump is still a ground jump.
  const p = world.players[i]!
  const grounded = p.grounded || world.tick <= p.coyoteUntil
  const candidates: Outcome[] = []
  for (const action of ['tap', 'hold'] as const) {
    const now = forecast(world, i, mem, view, action, 0, ob.clearX)
    if (!now.acted) return 0
    if (now.ok) {
      // On the ground (or in coyote time), wait only while a later jump still leaves the ground:
      // walking off to rely on the air jump at the last moment leaves no rescue for a late
      // reaction, and drops the bot off its tier (a rope bridge's missing planks over rafts).
      const later = forecast(world, i, mem, view, action, waitProbe(mem), ob.clearX, grounded)
      if (!blocked && later.ok) return 0
      return decide(world, mem, action)
    }
    candidates.push(now)
  }
  // Stuck against a wall, staying put only delays the trouble forever: jump as high as possible.
  if (blocked) return press(mem, 'hold')
  const later = forecast(world, i, mem, view, 'hold', 1, ob.clearX)
  if (later.acted && view.run.at > 1) return 0
  const [tap, hold] = candidates
  if (hold!.at > view.run.at && hold!.at >= tap!.at) return press(mem, 'hold')
  if (tap!.at > view.run.at) return press(mem, 'tap')
  return 0
}

function press(mem: BotMemory, action: 'tap' | 'hold'): InputBits {
  mem.holdJump = action === 'hold'
  return JUMP
}

/**
 * Jump over a drone locked on the bot (D-044: it keeps its height once locked), at the last tick
 * a full jump still carries the bot over it: wait while a jump one tick later would too.
 */
function dodgeDrone(world: World, i: number, mem: BotMemory, view: View): boolean {
  const pr = view.drone
  const p = world.players[i]!
  if (!pr || !p.grounded || (p.prevBits & JUMP) !== 0) return false
  if (!jumpDodges(world, i, pr, 0) || jumpDodges(world, i, pr, 1)) return false
  mem.holdJump = true
  return true
}

/**
 * Empty-handed with a crate ahead that running would miss (above head height): jump at the
 * last tick a full-height jump still touches it and lands past it.
 */
function reachCrate(world: World, i: number, mem: BotMemory, view: View, tier: BotTier): InputBits {
  const p = world.players[i]!
  if (p.item !== null || !p.grounded) return 0
  const c = crateAhead(world, i, tier.lookaheadPx)
  if (!c) return 0
  const size = world.cfg.items.crateSizePx
  const touch = { x: c.x, y: c.y, w: size, h: size }
  const goalX = c.x + size
  const base = plan(view, mem, 'hold', 0, goalX)
  const reach = (action: 'run' | 'hold', delay: number): boolean => {
    const o = rollout(world, i, view.scene, { ...base, action, delay, touch })
    return o.ok && o.touched
  }
  if (reach('run', 0) || !reach('hold', 0) || reach('hold', 1)) return 0
  return press(mem, 'hold')
}

/** Summon the ride (PET) once the meter is full, on the ground with nothing in the look-ahead. */
function rideBits(world: World, i: number, view: View): InputBits {
  const p = world.players[i]!
  if (!p.grounded || view.obstacle !== null || (p.prevBits & PET) !== 0) return 0
  return canSummon(world, p) ? PET : 0
}

/**
 * Items: a delay is drawn on pickup; once it has passed, the item is used as soon as its rule
 * allows (one ITEM press edge at a time).
 */
function itemBits(
  world: World,
  i: number,
  mem: BotMemory,
  view: View,
  tier: BotTier,
  rng: Rng,
): InputBits {
  const p = world.players[i]!
  if (p.item === null) {
    mem.itemUseAtTick = NO_TICK
    return 0
  }
  if (mem.itemUseAtTick === NO_TICK) {
    mem.itemUseAtTick = world.tick + nextRange(rng, tier.itemDelayMinTicks, tier.itemDelayMaxTicks)
  }
  if (world.tick < mem.itemUseAtTick || world.tick < p.itemCooldownUntil) return 0
  if ((p.prevBits & ITEM) !== 0) return 0
  return wantsToUse(world, i, p.item, view, tier) ? ITEM : 0
}

/**
 * Trip Wire and Oil Slick only near the front (rank ≤ `trapWhenRankAtMost`), else held; Spike Ball
 * and Homing Drone only with a player ahead; Boost Jet and Magnet on open ground; Shield Bubble
 * right away; Leader Missile from last place once the leader is `missileMinLeadTicks` of running
 * ahead (T3.12).
 */
function wantsToUse(world: World, i: number, item: ItemId, view: View, tier: BotTier): boolean {
  const p = world.players[i]!
  switch (item) {
    case 'shield_bubble':
      return true
    case 'boost_jet':
      return p.grounded && view.obstacle === null
    case 'trip_wire':
    case 'oil_slick':
      return p.rank <= tier.trapWhenRankAtMost
    case 'spike_ball':
      return playerAhead(world, i)
    case 'homing_drone':
      return droneTarget(world, i) !== NO_PLAYER
    case 'magnet':
      return p.grounded && view.obstacle === null
    case 'leader_missile': {
      const leader = leaderOf(world, i)
      if (leader === NO_PLAYER || p.rank < world.players.length) return false
      const lead = world.players[leader]!.progress - p.progress
      return lead >= tier.missileMinLeadTicks * world.cfg.baseSpeedPxPerTick
    }
  }
}
